import { KodallClient } from "@kodall/kodall-client";
import { isProblem, type OpenIdProvider, type Problem, type UserProfile } from "@kodall/kodall-client/dist/types";
import { useAuthStore } from "~/stores/auth";
import { oidcAccessToken, oidcLoginUrl, oidcLogout, oidcRefreshToken } from "~/utils/openid";
import { appendSlashIfRequired } from "~/utils/url";


let lastActionTime = 0
let sessionInterval: NodeJS.Timeout
let sessionRefreshTimeout = 2 * 60
const sessionMaxInactive = 1000 * 60 * 25

const frontendTokenRefresh = false

const logSessionRefreshTimeout = 
  () => console.log("Session refresh timeout: " + Math.round(sessionRefreshTimeout/1000) + "s.")
  

export default function useSecurity() {
  const auth = useAuthStore()
  auth.loadFromStorage() 
  const kc = useState<KodallClient>("kc", () => new KodallClient())
  const config = useRuntimeConfig()

  const return_url = appendSlashIfRequired(location.origin)
    + appendSlashIfRequired(config.app.baseURL)
    + "openid"

  const monitorEvents: (keyof DocumentEventMap)[] = ["mousedown", "keydown", "scroll", "focusin", "touchstart"]
  monitorEvents.forEach(e => document.addEventListener(e, () => lastActionTime = Date.now()), { passive: true})
  const inactiveTime = () => (Date.now() - lastActionTime)
  
  const logout = async () => {
    var result = await kc.value.logout()

    if (isProblem(result)) {
      return
    }

    if(auth.user?.roles?.includes("Client Standalone")) {
      auth.setUnauthenticated()
      useAccessibility().resetSettings()
      useRouter().push("/login")
      return
    }
    
    let oidcIssuer = await kc.value.getOidcIssuer();
    if (!oidcIssuer || isProblem(oidcIssuer)) {
      auth.setUnauthenticated()
      return
    }
    oidcLogout("account", oidcIssuer.end_session_endpoint)    
    auth.setUnauthenticated()
    useAccessibility().resetSettings()
  }

  const refreshOrLogout = async () => {
    if (inactiveTime() > sessionMaxInactive && !document.hasFocus()) {
      console.log("Inactive time: " + Math.floor(inactiveTime()/1000)  + "s "
        + "is larger than " + Math.floor(sessionMaxInactive/1000) + "s. Logging out.")
      logout()
      return
    }

    if (frontendTokenRefresh) {
      let oidcIssuer = await kc.value.getOidcIssuer();
      if (oidcIssuer && !isProblem(oidcIssuer)) {
        let refreshed = await oidcRefreshToken("account", oidcIssuer.token_endpoint);
        if (!refreshed) {
          logout()
          return
        }
      }
    }

    logSessionRefreshTimeout()
    if (isProblem(await kc.value.refreshSession())) {
      logout()
    }
  }

  const initialize = async(): Promise<{ authenticated: boolean, redirectUrl?: string, isStandalone?: boolean }> => {
    let session = await kc.value.session({roles: true})
    let redirectUrl = "/login"

    if (isProblem(session) || !session) {
      let oidcIssuer = await kc.value.getOidcIssuer();

      if (oidcIssuer && !isProblem(oidcIssuer)) {
        redirectUrl = await oidcLoginUrl("account", oidcIssuer.authorization_endpoint, return_url)
      }
      auth.setUnauthenticated()
      return { authenticated: false, redirectUrl }
    }

    const newSession = await kc.value.session({roles: true})

    if(isProblem(newSession)) {
      auth.setUnauthenticated()
      return { authenticated: false, redirectUrl }
    }

    if (!newSession.userKey) {
      auth.setUnauthenticated()
      return { authenticated: false, redirectUrl }
    }

    sessionRefreshTimeout = 1000 * ((session.expiresIn ?? 0)  * 0.9)
    logSessionRefreshTimeout()

    clearInterval(sessionInterval)
    sessionInterval = setInterval(refreshOrLogout, sessionRefreshTimeout);
    lastActionTime = Date.now()
    auth.setAuthenticated(newSession)

    return { authenticated: true, isStandalone: newSession.roles?.includes('Client Standalone') ?? false }
  } 
  
  return {
    logout,    
    initialize,
    kc,
    async requestOpenIDToken(): Promise<boolean> {
      let oidcIssuer = await kc.value.getOidcIssuer();
      if (isProblem(oidcIssuer) || !oidcIssuer) {
        return false
      }

      return await oidcAccessToken("account", oidcIssuer.token_endpoint, return_url, frontendTokenRefresh)
    },

    async login(user: string, password: string) {
      let result = await kc.value.auth({
        user: user,
        password: password
      });
      if (isProblem(result)) {
        return
      }
      auth.setAuthenticated(result)
      useRouter().push("/")
    },

    async getOpenIDProvider(): Promise<OpenIdProvider | null | Problem> {
      return kc.value.getOidcIssuer();
    }
  }
}